/**
 * CanvaNavigator — the REAL Canva folder navigator for every element input.
 *
 * Opens the SR Artwork Engine folder in the connected Canva account, drills into
 * subfolders (Side Shanks → Football → the many football designs), breadcrumbs
 * back, and picks a design. Thumbnails come from Canva's design metadata.
 * Reading is brokered by the JWT backend — the client never calls Canva itself.
 *
 * The trail is kept client-side so "back" always works even when the API returns
 * no breadcrumb; an API-supplied breadcrumb wins when present.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { browseCanva, browseCanvaDeep, filterTree } from './engine/pickerData.js'
import { CANVA_ROOT_ID, FOLDER_NAMES } from './engine/canvaFolders.js'
import styles from './Pickers.module.css'

/** Tiles rendered before "Show more" — 300+ previews at once is a mobile stall. */
const TILE_PAGE = 120

export default function CanvaNavigator({ rootFolder = CANVA_ROOT_ID, rootName, onPick }) {
  // trail = [{ id, name }, …] — the click path, deepest last.
  const [trail, setTrail] = useState(() => [
    { id: rootFolder, name: rootName || FOLDER_NAMES[rootFolder] || 'SR Artwork Engine' },
  ])
  // Flattened-subtree view. ON for an element folder — Bezel & Top Designs keeps
  // a dozen designs at its top level and the other 300+ in sport sub-folders, so
  // listing only the opened folder showed a fraction of the library. OFF at the
  // engine ROOT, where the folder structure is the thing being navigated.
  const [deep, setDeep] = useState(rootFolder !== CANVA_ROOT_ID)
  const [visible, setVisible] = useState(TILE_PAGE)
  const [node, setNode] = useState(null)
  const [loading, setLoading] = useState(true)
  const [paging, setPaging] = useState(false)
  const [q, setQ] = useState('')
  const seq = useRef(0)
  const nodeRef = useRef(null)

  const current = trail[trail.length - 1]
  nodeRef.current = node

  // Re-root when the input being overridden changes which folder we open on.
  useEffect(() => {
    setTrail([{ id: rootFolder, name: rootName || FOLDER_NAMES[rootFolder] || 'SR Artwork Engine' }])
    setDeep(rootFolder !== CANVA_ROOT_ID)
  }, [rootFolder, rootName])

  // A new folder or a new filter shows the top of the set, not page 3 of the last.
  useEffect(() => { setVisible(TILE_PAGE) }, [current.id, q, deep])

  // ONE folder, ALL of it. `browseCanva` follows the feed's continuation token
  // until it is empty; `onPage` puts each page on screen as it lands, so a
  // 300+ design folder starts rendering on page 1 instead of blocking on the
  // last one. `paging` is what says more is still coming.
  useEffect(() => {
    const ctrl = new AbortController()
    const mine = ++seq.current
    setLoading(true)
    setPaging(false)
    const progress = (page) => {
      if (seq.current !== mine) return
      setNode(page)
      setPaging(!!page.loading)
      setLoading(false)
    }
    // DEEP: every folder under this one, breadth-first, landing folder by folder.
    // SHALLOW: the original single-folder walk.
    ;(deep
      ? browseCanvaDeep(current.id, ctrl.signal, { onProgress: progress })
      : browseCanva(current.id, ctrl.signal, { onPage: progress })
    )
      .then((n) => {
        if (seq.current !== mine) return
        setNode(n)
        // Adopt the real folder name once the API tells us what it is.
        if (n?.name && n.name !== current.name) {
          setTrail((t) => t.map((c, i) => (i === t.length - 1 ? { ...c, name: n.name } : c)))
        }
      })
      .catch(() => { if (seq.current === mine) setNode(null) })
      .finally(() => { if (seq.current === mine) { setLoading(false); setPaging(false) } })
    return () => ctrl.abort()
    // `current.name` deliberately excluded — adopting the name must not refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current.id, deep])

  /** The page cap was hit — resume from the token the loop handed back. */
  async function loadMore() {
    const cur = nodeRef.current
    if (!cur?.continuation) return
    const mine = seq.current
    setPaging(true)
    try {
      const n = await browseCanva(cur.id, undefined, {
        seed: cur,
        continuation: cur.continuation,
        onPage: (page) => { if (seq.current === mine) setNode(page) },
      })
      if (seq.current === mine) setNode(n)
    } finally {
      if (seq.current === mine) setPaging(false)
    }
  }

  const breadcrumb = node?.breadcrumb?.length ? node.breadcrumb : trail

  const folders = useMemo(() => filterBy(node?.folders, q), [node, q])
  const allItems = node?.items || []
  // `filterTree` matches the design name OR the sub-folder it came from, so
  // typing "football" finds the football folder's designs whatever each one is
  // individually called. With no folder stamp it degrades to a name match.
  const items = useMemo(() => filterTree(allItems, q), [allItems, q])
  const tiles = items.slice(0, visible)

  function openFolder(f) {
    setQ('')
    // Clicking a crumb rewinds; clicking a child folder pushes.
    setTrail((t) => {
      const at = t.findIndex((c) => c.id === f.id)
      return at >= 0 ? t.slice(0, at + 1) : [...t, { id: f.id, name: f.name }]
    })
  }

  // A folder still paging is not an empty folder.
  const empty = !loading && !paging && !folders.length && !items.length

  return (
    <div className={styles.nav}>
      <div className={styles.navBar}>
        <div className={styles.crumbs}>
          {trail.length > 1 && (
            <button
              type="button"
              className={styles.upBtn}
              onClick={() => setTrail((t) => t.slice(0, -1))}
              title="Up one folder"
            >↑</button>
          )}
          {breadcrumb.map((b, i) => (
            <span key={`${b.id}-${i}`} className={styles.crumbWrap}>
              <button
                type="button"
                className={`${styles.crumb} ${i === breadcrumb.length - 1 ? styles.crumbOn : ''}`}
                onClick={() => openFolder(b)}
              >{b.name}</button>
              {i < breadcrumb.length - 1 && <span className={styles.crumbSep}>›</span>}
            </span>
          ))}
        </div>
        <input
          className={styles.navSearch}
          placeholder={deep ? 'Filter this folder and every folder in it…' : 'Filter this folder…'}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        {/* The narrower view is one click away — but it is not the default,
            because the designs live in the sub-folders. */}
        <button
          type="button"
          className={styles.upBtn}
          onClick={() => { setQ(''); setDeep((d) => !d) }}
          title={deep ? 'Show only this folder’s own designs' : 'Include every sub-folder’s designs'}
        >
          {deep ? 'This folder only' : 'Include sub-folders'}
        </button>
      </div>

      {node?.source === 'demo' && (
        <p className={styles.navDemo}>
          Demo library — the connected Canva account isn’t reachable right now.
          These are placeholders, not the real SR designs.
        </p>
      )}

      {loading ? (
        <div className={styles.navLoading}>Loading Canva folder…</div>
      ) : (
        <div className={styles.grid}>
          {folders.map((f) => (
            <button key={f.id} type="button" className={styles.folder} onClick={() => openFolder(f)}>
              <span className={styles.folderIcon}>📁</span>
              <span className={styles.tileName}>{f.name}</span>
            </button>
          ))}
          {tiles.map((it) => (
            <button
              key={it.id}
              type="button"
              className={styles.item}
              title={it.folderPath ? `${it.name} — ${it.folderPath}` : it.name}
              onClick={() =>
                onPick?.({
                  id: it.id,
                  name: it.name,
                  url: it.url || it.thumb,
                  thumb: it.thumb || it.url,
                  edit: it.edit || '',
                  source: 'canva',
                  // The folder the design ACTUALLY came from, which in the
                  // flattened view is a sub-folder, not the one on screen.
                  folderId: it.folderId || current.id,
                  folderName: it.folderName || current.name,
                })
              }
            >
              <span
                className={styles.itemThumb}
                style={{ backgroundImage: it.thumb ? `url("${it.thumb}")` : undefined }}
              />
              <span className={styles.tileName}>{it.name}</span>
              {deep && it.folderPath && it.folderId !== current.id && (
                <span className={styles.tileFolder}>{it.folderPath}</span>
              )}
            </button>
          ))}
          {empty && (
            <div className={styles.navEmpty}>
              {q ? 'Nothing matches that filter.' : 'This folder is empty in Canva.'}
            </div>
          )}
        </div>
      )}

      {/* THE FOLDER'S OWN RECEIPT — a count plus the state of the paging loop,
          so 300+ designs never read as "that is all there is", and a sub-folder
          is announced as something to open rather than something to pick. */}
      {!loading && (items.length > 0 || folders.length > 0 || paging) && (
        <p className={styles.navCount}>
          {folders.length > 0 && (
            <>{folders.length} folder{folders.length === 1 ? '' : 's'} to open{items.length || paging ? ' · ' : ''}</>
          )}
          {(items.length > 0 || paging) && (
            <>{items.length} design{items.length === 1 ? '' : 's'}</>
          )}
          {paging
            ? deep
              ? ' — reading this folder and its sub-folders…'
              : ' — loading the rest of this folder…'
            : node?.continuation
              ? ' — more to load.'
              : q.trim()
                ? ' matching this filter.'
                : deep && node?.scanned > 1
                  // The claim the whole fix exists to make good on.
                  ? ` across ${node.scanned} folders — everything under ${current.name}.`
                  : ''}
          {node?.truncated && ` ${node.reason}`}
          {node?.partial && !node?.truncated && ` ${node.reason || 'The feed stopped part-way; this is what loaded.'}`}
        </p>
      )}
      {items.length > tiles.length && (
        <button type="button" className={styles.upBtn} onClick={() => setVisible((v) => v + TILE_PAGE)}>
          Show {Math.min(TILE_PAGE, items.length - tiles.length)} more — {items.length - tiles.length} still hidden
        </button>
      )}
      {node?.continuation && !paging && (
        <button type="button" className={styles.upBtn} onClick={loadMore}>
          Load more designs
        </button>
      )}
    </div>
  )
}

function filterBy(list, q) {
  const arr = Array.isArray(list) ? list : []
  const needle = q.trim().toLowerCase()
  if (!needle) return arr
  return arr.filter((x) => (x.name || '').toLowerCase().includes(needle))
}
